import { useRef } from "react";
import { DuplicateIcon } from "../../materials/icons/duplicate-icon/DuplicateIcon";
import { Ellipsis } from "../ellipsis/Ellipsis";
import { useToast } from "../toast/Toast";
import { Tooltip } from "../tooltip/Tooltip";
import {
  CopyIconButton,
  CopyableContainer,
  Props,
  TooltipText,
  extractText,
  text,
} from "./Copyable.Parts";

export const Copyable = ({
  children,
  canCopy = true,
  autoHoverable = true,
  disableTooltip = false,
  tooltipText,
  disableToast = false,
  toastSuccessMessage,
  toastErrorMessage,
  ellipsis,
  copyText,
  onCopy,
  onCopyFailed,
  iconSize,
  ...props
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToast();
  const extractedText = extractText(children);
  const textToCopy = copyText ?? extractedText;
  const hasTextToCopy = textToCopy?.length > 0;
  const hoverable = autoHoverable && hasTextToCopy;
  const render = canCopy && hasTextToCopy;

  const tooltip =
    autoHoverable && (tooltipText ?? ellipsis) ? (
      <TooltipText>
        <span>{extractedText}</span>
        <span>{text.tooltip}</span>
      </TooltipText>
    ) : (
      text.tooltip
    );

  const handleClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      await navigator.clipboard.writeText(textToCopy);
      !disableToast &&
        addToast({
          text: toastSuccessMessage ?? text.confirmation,
          variant: "success",
        });
      onCopy?.();
    } catch (err) {
      !disableToast &&
        addToast({
          text: toastErrorMessage ?? text.copyError,
          variant: "error",
        });
      onCopyFailed?.();
    }
  };

  return render ? (
    <Tooltip
      tip={tooltip}
      open={!disableTooltip && hoverable ? undefined : false}
    >
      <CopyableContainer
        ref={containerRef}
        $hoverable={hoverable}
        {...props}
        onClick={hoverable ? handleClick : undefined}
      >
        {ellipsis ? (
          <Ellipsis disableTooltip={autoHoverable}>{children}</Ellipsis>
        ) : (
          <div>{children}</div>
        )}
        <Tooltip
          tip={tooltip}
          open={!disableTooltip && !hoverable ? undefined : false}
        >
          <CopyIconButton
            $disabled={hoverable}
            onClick={!hoverable ? handleClick : undefined}
            {...(iconSize ? { $height: iconSize, $width: iconSize } : {})}
          >
            <DuplicateIcon />
          </CopyIconButton>
        </Tooltip>
      </CopyableContainer>
    </Tooltip>
  ) : (
    children
  );
};
